import { EventEmitter } from 'events';

export interface MetricsConfig {
  enabled: boolean;
  collectors: string[];
  flushInterval: number;
  maxBufferSize: number;
  exporters: MetricsExporter[];
}

export interface Metric {
  name: string;
  type: 'counter' | 'gauge' | 'histogram' | 'summary';
  value: number;
  timestamp: Date;
  labels?: Record<string, string>;
  help?: string;
}

export interface MetricsExporter {
  export(metrics: Metric[]): Promise<void>;
  flush?(): Promise<void>;
  shutdown?(): Promise<void>;
}

export interface PerformanceMetric {
  operation: string;
  duration: number;
  success: boolean;
  timestamp: Date;
  context?: Record<string, any>;
}

export class MetricsCollector extends EventEmitter {
  private metrics: Map<string, Metric> = new Map();
  private buffer: Metric[] = [];
  private flushInterval: NodeJS.Timeout | null = null;
  private performanceBuffer: PerformanceMetric[] = [];

  constructor(private config: MetricsConfig) {
    super();
    
    if (config.enabled) {
      this.startFlushInterval();
    }
  }

  /**
   * Increment a counter metric
   */
  increment(name: string, labels?: Record<string, string>, help?: string): void {
    if (!this.config.enabled) return;

    const key = this.getMetricKey(name, labels);
    const existing = this.metrics.get(key);

    const metric: Metric = {
      name,
      type: 'counter',
      value: (existing?.value || 0) + 1,
      timestamp: new Date(),
      labels,
      help
    };

    this.metrics.set(key, metric);
    this.addToBuffer(metric);
    this.emit('metric:updated', metric);
  }

  /**
   * Set a gauge metric value
   */
  gauge(name: string, value: number, labels?: Record<string, string>, help?: string): void {
    if (!this.config.enabled) return;

    const key = this.getMetricKey(name, labels);
    const metric: Metric = {
      name,
      type: 'gauge',
      value,
      timestamp: new Date(),
      labels,
      help
    };

    this.metrics.set(key, metric);
    this.addToBuffer(metric);
    this.emit('metric:updated', metric);
  }

  /**
   * Record a histogram value
   */
  histogram(name: string, value: number, labels?: Record<string, string>, help?: string): void {
    if (!this.config.enabled) return;

    const metric: Metric = {
      name,
      type: 'histogram',
      value,
      timestamp: new Date(),
      labels,
      help
    };

    this.addToBuffer(metric);
    this.emit('metric:recorded', metric);
  }

  /**
   * Record performance metrics
   */
  recordPerformance(operation: string, duration: number, success: boolean = true, context?: Record<string, any>): void {
    if (!this.config.enabled) return;

    const performanceMetric: PerformanceMetric = {
      operation,
      duration,
      success,
      timestamp: new Date(),
      context
    };

    this.performanceBuffer.push(performanceMetric);
    
    // Convert to histogram metric
    this.histogram(`${operation}_duration_ms`, duration, {
      operation,
      success: success.toString(),
      ...context
    }, `Duration of ${operation} operation in milliseconds`);

    // Increment operation counter
    this.increment(`${operation}_total`, {
      operation,
      success: success.toString(),
      ...context
    }, `Total number of ${operation} operations`);

    this.emit('performance:recorded', performanceMetric);

    // Emit slow operation warning
    if (duration > 5000) { // 5 seconds threshold
      this.emit('performance:slow', {
        operation,
        duration,
        threshold: 5000,
        context
      });
    }
  }

  /**
   * Start a timer for an operation
   */
  startTimer(operation: string, context?: Record<string, any>): () => void {
    const startTime = Date.now();
    
    return (success: boolean = true) => {
      const duration = Date.now() - startTime;
      this.recordPerformance(operation, duration, success, context);
    };
  }

  /**
   * Decorator for automatic performance tracking
   */
  trackPerformance(operation?: string) {
    return (target: any, propertyKey: string, descriptor: PropertyDescriptor) => {
      const originalMethod = descriptor.value;
      const operationName = operation || `${target.constructor.name}.${propertyKey}`;

      descriptor.value = async function (...args: any[]) {
        // Try to get metrics collector from the instance or create a no-op timer
        const metricsCollector = (this as any).metricsCollector || (this as any).metrics;
        const timer = metricsCollector?.startTimer ? metricsCollector.startTimer(operationName) : (() => {});
        let success = true;
        
        try {
          const result = await originalMethod.apply(this, args);
          return result;
        } catch (error) {
          success = false;
          throw error;
        } finally {
          timer(success);
        }
      };

      return descriptor;
    };
  }

  /**
   * Add metric to buffer
   */
  private addToBuffer(metric: Metric): void {
    this.buffer.push(metric);

    if (this.buffer.length >= this.config.maxBufferSize) {
      this.flush();
    }
  }

  /**
   * Start automatic flush interval
   */
  private startFlushInterval(): void {
    this.flushInterval = setInterval(() => {
      this.flush();
    }, this.config.flushInterval);
  }

  /**
   * Flush metrics to exporters
   */
  async flush(): Promise<void> {
    if (this.buffer.length === 0) return;

    const metricsToFlush = [...this.buffer];
    this.buffer = [];

    this.emit('metrics:flushing', { count: metricsToFlush.length });

    for (const exporter of this.config.exporters) {
      try {
        await exporter.export(metricsToFlush);
      } catch (error) {
        this.emit('exporter:error', { exporter: exporter.constructor.name, error });
      }
    }

    this.emit('metrics:flushed', { count: metricsToFlush.length });
  }

  /**
   * Get current metric values
   */
  getMetrics(): Metric[] {
    return Array.from(this.metrics.values());
  }

  /**
   * Get performance metrics
   */
  getPerformanceMetrics(): PerformanceMetric[] {
    return [...this.performanceBuffer];
  }

  /**
   * Clear all metrics
   */
  clear(): void {
    this.metrics.clear();
    this.buffer = [];
    this.performanceBuffer = [];
    this.emit('metrics:cleared');
  }

  /**
   * Generate unique key for metric
   */
  private getMetricKey(name: string, labels?: Record<string, string>): string {
    if (!labels) return name;
    
    const sortedLabels = Object.entries(labels)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, value]) => `${key}=${value}`)
      .join(',');
    
    return `${name}{${sortedLabels}}`;
  }

  /**
   * Shutdown metrics collector
   */
  async shutdown(): Promise<void> {
    if (this.flushInterval) {
      clearInterval(this.flushInterval);
      this.flushInterval = null;
    }

    // Final flush
    await this.flush();

    // Shutdown exporters
    for (const exporter of this.config.exporters) {
      if (exporter.shutdown) {
        try {
          await exporter.shutdown();
        } catch (error) {
          this.emit('exporter:shutdown:error', { exporter: exporter.constructor.name, error });
        }
      }
    }

    this.emit('metrics:shutdown');
  }
}

/**
 * Console exporter for development
 */
export class ConsoleExporter implements MetricsExporter {
  constructor(private options: { interval?: number; detailed?: boolean } = {}) {}

  async export(metrics: Metric[]): Promise<void> {
    if (metrics.length === 0) return;

    console.log('\n--- Metrics Export ---');
    console.log(`Timestamp: ${new Date().toISOString()}`);
    console.log(`Metrics Count: ${metrics.length}`);

    if (this.options.detailed) {
      for (const metric of metrics) {
        const labels = metric.labels 
          ? ` {${Object.entries(metric.labels).map(([k, v]) => `${k}="${v}"`).join(', ')}}` 
          : '';
        console.log(`${metric.name}${labels}: ${metric.value} (${metric.type})`);
      }
    } else {
      // Summary view
      const summary = metrics.reduce((acc, metric) => {
        acc[metric.type] = (acc[metric.type] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);

      console.log('Summary by type:', summary);
    }

    console.log('----------------------\n');
  }
}

/**
 * Prometheus-compatible exporter
 */
export class PrometheusExporter implements MetricsExporter {
  private server: any = null;

  constructor(private options: { port?: number; endpoint?: string } = {}) {}

  async export(metrics: Metric[]): Promise<void> {
    // This would integrate with actual Prometheus client
    // For now, just format the metrics
    const prometheusFormat = this.formatForPrometheus(metrics);
    
    if (!this.server) {
      this.startServer();
    }
  }

  private formatForPrometheus(metrics: Metric[]): string {
    const lines: string[] = [];

    for (const metric of metrics) {
      if (metric.help) {
        lines.push(`# HELP ${metric.name} ${metric.help}`);
      }
      lines.push(`# TYPE ${metric.name} ${metric.type}`);

      const labels = metric.labels 
        ? `{${Object.entries(metric.labels).map(([k, v]) => `${k}="${v}"`).join(',')}}` 
        : '';
      
      lines.push(`${metric.name}${labels} ${metric.value} ${metric.timestamp.getTime()}`);
    }

    return lines.join('\n');
  }

  private startServer(): void {
    // This would start an HTTP server for Prometheus scraping
    console.log(`Prometheus exporter would start server on port ${this.options.port || 9090}`);
  }

  async shutdown(): Promise<void> {
    if (this.server) {
      // Close server
      this.server = null;
    }
  }
}

/**
 * JSON file exporter
 */
export class FileExporter implements MetricsExporter {
  constructor(private filePath: string) {}

  async export(metrics: Metric[]): Promise<void> {
    const fs = require('fs').promises;
    
    try {
      const data = {
        timestamp: new Date().toISOString(),
        metrics: metrics.map(m => ({
          ...m,
          timestamp: m.timestamp.toISOString()
        }))
      };

      await fs.writeFile(this.filePath, JSON.stringify(data, null, 2));
    } catch (error) {
      console.error('Failed to export metrics to file:', error);
    }
  }
}

/**
 * Built-in metric collectors for common WhatsApp operations
 */
export const WhatsAppMetrics = {
  /**
   * Message metrics collector
   */
  messages: (collector: MetricsCollector) => ({
    sent: (type: string, chatId: string) => {
      collector.increment('messages_sent_total', { type, chat_type: chatId.endsWith('@g.us') ? 'group' : 'individual' });
    },
    
    received: (type: string, chatId: string) => {
      collector.increment('messages_received_total', { type, chat_type: chatId.endsWith('@g.us') ? 'group' : 'individual' });
    },
    
    failed: (type: string, error: string) => {
      collector.increment('messages_failed_total', { type, error });
    },

    latency: (duration: number, type: string) => {
      collector.histogram('message_send_duration_ms', duration, { type });
    }
  }),

  /**
   * Session metrics collector
   */
  sessions: (collector: MetricsCollector) => ({
    connected: (tel: string) => {
      collector.gauge('session_connected', 1, { tel });
    },

    disconnected: (tel: string, reason: string) => {
      collector.gauge('session_connected', 0, { tel });
      collector.increment('session_disconnections_total', { tel, reason });
    },

    qrGenerated: (tel: string) => {
      collector.increment('session_qr_generated_total', { tel });
    },

    restarts: (tel: string, reason: string) => {
      collector.increment('session_restarts_total', { tel, reason });
    }
  }),

  /**
   * API metrics collector
   */
  api: (collector: MetricsCollector) => ({
    request: (endpoint: string, method: string, statusCode: number, duration: number) => {
      collector.increment('api_requests_total', { endpoint, method, status: statusCode.toString() });
      collector.histogram('api_request_duration_ms', duration, { endpoint, method });
    },

    error: (endpoint: string, method: string, error: string) => {
      collector.increment('api_errors_total', { endpoint, method, error });
    }
  })
};